import React, { useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { UseQueryState } from 'urql';
import BlockContent from '@sanity/block-content-to-react';
import { Button, Icon } from '@global-ecom/nitro-uds/elements';

import { Page } from 'ui/components/Page';
import { Link } from 'ui/elements/Link';
import {
  Prose,
  serializeHeaderToList,
  serializeTocBlockContent,
} from 'utils/serializeBlockContent';
import { useDialogDismiss, useFocusTrap } from 'hooks/a11y';
import { QueryContentArgs } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { PageHead } from 'ui/components/PageHead';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { CustomerSupportBanner } from 'ui/components/CustomerSupportBanner';
import { ContentResponse, useGroqQuery } from 'hooks/useGroqQuery';
import {
  SupportPageContent,
  SupportPagesContent,
  SupportPagesQuery,
} from 'groq/pages/SupportPage';
import { GaTrackData, usePromotionView } from 'hooks/usePromotionView';
import { LocalePortableTextType } from 'groq/shared/LocalePortableText';

const MobileNav = ({
  supportPageIndex,
  linkQueryString,
  onClick,
  sidebarContent,
}: {
  supportPageIndex: UseQueryState<
    ContentResponse<SupportPagesContent>,
    QueryContentArgs
  >;
  linkQueryString: string;
  onClick?: ((e: Event) => void) | undefined;
  sidebarContent?: LocalePortableTextType | undefined;
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const t = useTranslate();
  const ref = useRef<HTMLDivElement>(null);

  useDialogDismiss({
    ref: ref,
    handler: () => setDropdownOpen(false),
  });

  useFocusTrap({ ref: ref, isActive: isDropdownOpen });

  return (
    <div className="md:hidden" data-test-id="support-page-mobile-nav">
      <section>
        <div className="md:hidden flex items-center bg-puma-black-700 p-2">
          <h2
            id="support-page-nav-dropdown-title"
            className="font-bold flex-grow px-4"
          >
            {t('supportPagesDropdownTitle')}
          </h2>
          <Button
            name={t('supportPagesDropdownTitle')}
            aria-haspopup="true"
            aria-expanded={isDropdownOpen}
            aria-controls="support-page-nav-dropdown"
            data-test-id="support-page-nav-dropdown-button"
            className="bg-puma-black-800 p-2 rounded-sm"
            onClick={() => {
              setDropdownOpen(!isDropdownOpen);
            }}
            label={
              isDropdownOpen
                ? 'support pages dropdown is open'
                : 'support pages dropdown is closed'
            }
            icon={isDropdownOpen ? 'subtract' : 'add'}
            mode="icon"
          />
        </div>
        {isDropdownOpen && (
          <ul
            key="support-page-nav-dropdown"
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-page-nav-dropdown-title"
            tabIndex={-1}
            id="support-page-nav-dropdown"
            data-test-id="support-page-nav-dropdown"
            className="bg-puma-black-800 p-6 divide-y"
          >
            {supportPageIndex.data?.content?.map(
              ({ _id, title, url, localeTitle }) => {
                return (
                  <li key={_id}>
                    <Link
                      onClick={e => {
                        onClick?.(e);
                        setDropdownOpen(false);
                      }}
                      className="font-bold block py-2"
                      href={`${url}${linkQueryString}`}
                    >
                      {localeTitle || title}
                    </Link>
                  </li>
                );
              }
            )}
          </ul>
        )}
      </section>
      {sidebarContent && (
        <div className="mt-2 p-2 bg-puma-black-800">
          <BlockContent
            blocks={sidebarContent}
            serializers={serializeTocBlockContent}
          />
        </div>
      )}
    </div>
  );
};

const DesktopNav = ({
  headers,
  supportPageIndex,
  linkQueryString,
  onClick,
  sidebarContent,
  currentSupportPage,
}: {
  supportPageIndex: UseQueryState<
    ContentResponse<SupportPagesContent>,
    QueryContentArgs
  >;
  linkQueryString: string;
  onClick?: ((e: Event) => void) | undefined;
  sidebarContent?: LocalePortableTextType | undefined;
  headers: LocalePortableTextType;
  currentSupportPage: string;
}) => {
  return (
    <div
      className="hidden md:block space-y-6 pr-6 border-r-2"
      data-test-id="support-page-desktop-nav"
    >
      <section>
        <ul>
          {supportPageIndex.data?.content?.map(
            ({ _id, title, url, localeTitle }) => {
              const isActive = _id === currentSupportPage;

              return (
                <li key={_id}>
                  <Link
                    className={`font-bold hover:underline ${
                      isActive ? 'underline' : ''
                    }`}
                    href={`${url}${linkQueryString}`}
                    onClick={onClick}
                  >
                    {localeTitle || title}
                  </Link>
                </li>
              );
            }
          )}
        </ul>
      </section>
      <section className="hidden md:block">
        <ul>
          <BlockContent
            renderContainerOnSingleChild
            blocks={headers}
            serializers={serializeHeaderToList}
          />
        </ul>
      </section>
      {sidebarContent && (
        <BlockContent
          blocks={sidebarContent}
          serializers={serializeTocBlockContent}
        />
      )}
    </div>
  );
};

const Integration = ({ type }: { type?: string }) => {
  switch (type) {
    case 'yext':
      return (
        <>
          <Head>
            <script src="https://answers-embed.us.puma.com.pagescdn.com/iframe-prod.js"></script>
          </Head>
          <div id="answers-container"></div>
        </>
      );
    default:
      return null;
  }
};

export const SupportPage = ({
  supportPageQuery,
  onLinksClick,
}: {
  supportPageQuery: UseQueryState<
    ContentResponse<SupportPageContent>,
    QueryContentArgs
  >;
  onLinksClick?: (e: Event) => void;
}) => {
  const t = useTranslate();
  const router = useRouter();
  const { localizeUrlPath } = useSiteConfig();

  const [supportPageIndex] = useGroqQuery({
    operationName: 'SupportPages',
    query: SupportPagesQuery,
    variables: { platform: 'web' },
  });

  const supportPage = supportPageQuery?.data?.content;
  const supportPageContent =
    supportPage?.localeContent ?? supportPage?.content ?? [];

  const headers = supportPage?.generateSidebarNavigation
    ? supportPageContent?.filter(
        block => 'style' in block && block.style && /^h\d/.test(block.style)
      )
    : [];

  const [, queryString] = router.asPath.split('?');
  const qs = new URLSearchParams(queryString);

  const showMinimalNav = qs.has('minimal');

  let returnUrl = localizeUrlPath('/');
  let returnText = t('home');

  if (qs.get('return') === 'livechat') {
    returnUrl = localizeUrlPath('/live-chat');
    returnText = t('returnToChat');
  }

  const linkQueryString = queryString ? `?${queryString}` : '';

  const supportPageRef = useRef(null);
  const gaTrackData: GaTrackData = {
    id: '',
    name: '',
    creative: 'TermsAndConditions',
  };
  usePromotionView(supportPageRef, gaTrackData, true);

  const pageTitle = t('pageTitleReduced', {
    title:
      supportPage?.seo?.pageTitles ||
      supportPage?.localeTitle ||
      supportPage?.title ||
      '',
  });

  return (
    <div ref={supportPageRef}>
      <PageHead
        title={pageTitle}
        description={supportPage?.seo?.pageDescriptions}
      />
      {showMinimalNav && (
        <div
          data-test-id="support-page-minimal-nav"
          className="mobile:p-4 tablet:p-6 desktop:p-8 border-b-1"
        >
          <a href={returnUrl} className="flex items-center gap-4">
            <Icon name="chevron-left" size="2xl" />
            {returnText}
          </a>
        </div>
      )}
      <Page
        query={{
          ...supportPageQuery,
          fetching: supportPageQuery.fetching || supportPageQuery.stale,
        }}
      >
        {supportPage && (
          <div className="w-full max-w-screen-xl mx-auto grid gap-6 md:gap-10 lg:gap-16 md:grid-cols-12 xl:pt-4 xl:pb-16">
            <nav
              aria-label="Support page navigation"
              className="md:col-span-4 lg:col-span-3"
            >
              <MobileNav
                supportPageIndex={supportPageIndex}
                linkQueryString={linkQueryString}
                onClick={onLinksClick}
                sidebarContent={supportPage?.sidebarContent}
              />
              <DesktopNav
                headers={headers}
                supportPageIndex={supportPageIndex}
                linkQueryString={linkQueryString}
                onClick={onLinksClick}
                sidebarContent={supportPage?.sidebarContent}
                currentSupportPage={supportPage._id}
              />
            </nav>
            <article className="md:col-span-8 lg:col-span-9">
              <Prose
                content={
                  supportPage.localeContent
                    ? supportPage.localeContent
                    : supportPage.content
                }
              />
              <Integration type={supportPage.integration} />
            </article>
            <CustomerSupportBanner />
          </div>
        )}
      </Page>
    </div>
  );
};
